import React, { useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Plus,
  Trash2,
  Network,
  ArrowRight,
  ArrowLeft,
  AlertCircle,
  Loader2,
  Eye,
  ScanSearch,
  Wifi,
  Users,
  Package,
  Monitor,
  Shield,
  ChevronDown,
  ChevronRight,
} from 'lucide-react';
import { useAssetRelationships } from '@/hooks/useAssetRelationships';
import { RELATIONSHIP_TYPES, IMPORTANCE_LEVELS, AssetRelationship } from '@/types/asset-relationship';
import { AddRelationshipDialog } from './AddRelationshipDialog';
import { AssetRelationshipGraph } from './AssetRelationshipGraph';
import { useDiscoveredItems, buildDiscoveryGraphData } from './useDiscoveredItems';
import { 
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

// ─── Helper sub-component ────────────────────────────────────────────────────

function DiscoverySection({
  icon, label, count, expanded, onToggle, children,
}: {
  icon: React.ReactNode;
  label: string;
  count: number;
  expanded: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}) {
  return (
    <div className="border rounded-lg overflow-hidden">
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center justify-between px-3 py-2 bg-gray-50 hover:bg-gray-100 transition-colors text-left"
      >
        <div className="flex items-center gap-2">
          {icon}
          <span className="text-sm font-medium text-gray-700">{label}</span>
          <Badge variant="secondary" className="text-xs ml-1">{count}</Badge>
        </div>
        {expanded
          ? <ChevronDown className="h-4 w-4 text-gray-400" />
          : <ChevronRight className="h-4 w-4 text-gray-400" />}
      </button>
      {expanded && (
        <div className="px-3 py-2 max-h-44 overflow-y-auto space-y-px bg-white">
          {children}
        </div>
      )}
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

interface AssetRelationshipsProps {
  assetId: string;
  assetName: string;
}

export const AssetRelationships = ({ assetId, assetName }: AssetRelationshipsProps) => {
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showGraphView, setShowGraphView] = useState(false);
  const [deletingRelationship, setDeletingRelationship] = useState<string | null>(null);
  const [showDiscovery, setShowDiscovery] = useState(true);
  const [expandedDisc, setExpandedDisc] = useState<Set<string>>(new Set());

  const {
    relationships,
    isLoading,
    error,
    deleteRelationship,
    graphData,
    isLoadingGraph,
  } = useAssetRelationships(assetId);

  const { data: discSummary } = useDiscoveredItems(assetId);

  // Merge discovery virtual nodes into the graph when toggle is on
  const mergedGraphData = useMemo(() => {
    if (!graphData) return graphData;
    if (!showDiscovery || !discSummary) return graphData;
    const { nodes: discNodes, edges: discEdges } = buildDiscoveryGraphData(assetId, discSummary);
    return {
      nodes: [...graphData.nodes, ...discNodes],
      edges: [...graphData.edges, ...discEdges],
    };
  }, [graphData, discSummary, showDiscovery, assetId]);

  const toggleDisc = (key: string) =>
    setExpandedDisc(prev => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });

  const handleDeleteRelationship = async (relationshipId: string) => {
    try {
      await deleteRelationship.mutateAsync(relationshipId);
      setDeletingRelationship(null);
    } catch (error) {
      console.error('Erro ao deletar relacionamento:', error);
    }
  };

  const getRelationshipDirection = (relationship: AssetRelationship) => {
    const isSource = relationship.source_asset_id === assetId;
    return {
      isSource,
      relatedAsset: isSource ? relationship.target_asset : relationship.source_asset,
      direction: isSource ? 'outgoing' : 'incoming',
    };
  };

  const getImportanceColor = (importance: string) => {
    const level = IMPORTANCE_LEVELS[importance as keyof typeof IMPORTANCE_LEVELS];
    return level?.color || 'gray';
  };

  const getImportanceBadgeVariant = (importance: string) => {
    switch (importance) {
      case 'critical':
        return 'destructive';
      case 'high':
        return 'default';
      case 'medium':
        return 'secondary';
      case 'low':
        return 'outline';
      default:
        return 'outline';
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-6 w-6 animate-spin mr-2" />
        <span>Carregando relacionamentos...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center p-8 text-red-600">
        <AlertCircle className="h-6 w-6 mr-2" />
        <span>Erro ao carregar relacionamentos</span>
      </div>
    );
  }

  return (
    <div className="space-y-6 h-full flex flex-col">
      {/* Header com controles */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold">Relacionamentos do Ativo</h3>
          <p className="text-sm text-muted-foreground">
            Gerencie as dependências e conexões de <strong>{assetName}</strong>
          </p>
        </div>
        <div className="flex gap-2">
          {discSummary && (
            <Button
              variant={showDiscovery ? 'default' : 'outline'}
              size="sm"
              onClick={() => setShowDiscovery(v => !v)}
              className={showDiscovery ? 'bg-blue-600 hover:bg-blue-700' : ''}
            >
              <ScanSearch className="h-4 w-4 mr-2" />
              Descobertos
            </Button>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowGraphView(!showGraphView)}
            disabled={isLoadingGraph}
          >
            {isLoadingGraph ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <Eye className="h-4 w-4 mr-2" />
            )}
            {showGraphView ? 'Lista' : 'Grafo'}
          </Button>
          <Button
            onClick={() => setShowAddDialog(true)}
            size="sm"
          >
            <Plus className="h-4 w-4 mr-2" />
            Adicionar Relacionamento
          </Button>
        </div>
      </div>

      {showGraphView ? (
        /* Visualização em Grafo */
        <Card className="flex-1 flex flex-col min-h-0">
          <CardHeader className="flex-shrink-0">
            <CardTitle className="flex items-center gap-2">
              <Network className="h-5 w-5" />
              Mapa de Relacionamentos
            </CardTitle>
          </CardHeader>
          <CardContent className="flex-1 overflow-hidden">
            <AssetRelationshipGraph
              graphData={mergedGraphData}
              isLoading={isLoadingGraph}
              centerAssetId={assetId}
            />
          </CardContent>
        </Card>
      ) : (
        /* Visualização em Lista */
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span className="flex items-center gap-2">
                <Network className="h-5 w-5" />
                Lista de Relacionamentos
              </span>
              <Badge variant="secondary">
                {relationships.length} relacionamento{relationships.length !== 1 ? 's' : ''}
              </Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {relationships.length === 0 ? (
              <div className="text-center py-8">
                <Network className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <h4 className="text-lg font-medium mb-2">Nenhum relacionamento encontrado</h4>
                <p className="text-muted-foreground mb-4">
                  Este ativo ainda não possui relacionamentos configurados.
                </p>
                <Button onClick={() => setShowAddDialog(true)}>
                  <Plus className="h-4 w-4 mr-2" />
                  Adicionar Primeiro Relacionamento
                </Button>
              </div>
            ) : (
              <ScrollArea className="h-[400px]">
                <div className="space-y-4">
                  {relationships.map((relationship) => {
                    const { isSource, relatedAsset, direction } = getRelationshipDirection(relationship);
                    const relationshipType = RELATIONSHIP_TYPES[relationship.relationship_type];
                    
                    return (
                      <div
                        key={relationship.id}
                        className="flex items-center justify-between p-4 border rounded-lg hover:bg-muted/50 transition-colors"
                      >
                        <div className="flex items-center gap-4 flex-1">
                          {/* Direção do relacionamento */}
                          <div className="flex items-center gap-2">
                            {direction === 'outgoing' ? (
                              <ArrowRight className="h-4 w-4 text-blue-600" />
                            ) : (
                              <ArrowLeft className="h-4 w-4 text-green-600" />
                            )}
                          </div>

                          {/* Informações do relacionamento */}
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="font-medium">
                                {relatedAsset?.nome || 'Ativo não encontrado'}
                              </span>
                              {relatedAsset?.tipo_nome && !/^[0-9a-f]{8}-[0-9a-f]{4}-/.test(relatedAsset.tipo_nome) && (
                                <Badge variant="outline" className="text-xs">
                                  {relatedAsset.tipo_nome}
                                </Badge>
                              )}
                            </div>
                            
                            <div className="flex items-center gap-2 text-sm text-muted-foreground">
                              <span className="font-medium text-foreground">
                                {relationshipType?.label || relationship.relationship_type}
                              </span>
                              {relationship.description && (
                                <>
                                  <span>•</span>
                                  <span>{relationship.description}</span>
                                </>
                              )}
                            </div>
                          </div>

                          {/* Nível de importância */}
                          <Badge 
                            variant={getImportanceBadgeVariant(relationship.importance_level)}
                            className="ml-auto"
                          >
                            {IMPORTANCE_LEVELS[relationship.importance_level]?.label || relationship.importance_level}
                          </Badge>
                        </div>

                        {/* Ações */}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setDeletingRelationship(relationship.id)}
                          className="ml-2 text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    );
                  })}
                </div>
              </ScrollArea>
            )}
          </CardContent>
        </Card>
      )}

      {/* ── Itens Descobertos na lista ── */}
      {!showGraphView && showDiscovery && discSummary && (
        <Card>
          <CardHeader className="pb-3 pt-4 px-5">
            <CardTitle className="text-sm flex items-center gap-2">
              <ScanSearch className="h-4 w-4 text-blue-500" />
              Descobertos pelo Agente
              {discSummary.lastSeenAt && (
                <span className="text-xs font-normal text-muted-foreground ml-1">
                  · coleta de {new Date(discSummary.lastSeenAt).toLocaleDateString('pt-BR')}
                </span>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent className="px-5 pb-4 space-y-2">
            {/* Rede */}
            {discSummary.networkCount > 0 && (
              <DiscoverySection
                icon={<Wifi className="h-4 w-4 text-cyan-500" />}
                label="Interfaces de Rede"
                count={discSummary.networkCount}
                expanded={expandedDisc.has('net')}
                onToggle={() => toggleDisc('net')}
              >
                {discSummary.networks.map((n, i) => (
                  <div key={i} className="flex items-center justify-between text-xs py-0.5">
                    <span className="text-gray-700">{n.name}</span>
                    <span className="text-gray-400 font-mono">{n.ip || n.mac || ''}</span>
                  </div>
                ))}
              </DiscoverySection>
            )}
            {/* Usuários */}
            {discSummary.userCount > 0 && (
              <DiscoverySection
                icon={<Users className="h-4 w-4 text-purple-500" />}
                label="Usuários Locais"
                count={discSummary.userCount}
                expanded={expandedDisc.has('usr')}
                onToggle={() => toggleDisc('usr')}
              >
                {discSummary.users.map((u, i) => (
                  <div key={i} className="flex items-center justify-between text-xs py-0.5">
                    <span className="text-gray-700">{u.name}</span>
                    <Badge variant={u.enabled ? 'default' : 'secondary'} className="text-[10px] py-0 h-4">
                      {u.enabled ? 'Ativo' : 'Inativo'}
                    </Badge>
                  </div>
                ))}
              </DiscoverySection>
            )}
            {/* Software */}
            {discSummary.softwareCount > 0 && (
              <DiscoverySection
                icon={<Package className="h-4 w-4 text-orange-500" />}
                label="Software Instalado"
                count={discSummary.softwareCount}
                expanded={expandedDisc.has('sw')}
                onToggle={() => toggleDisc('sw')}
              >
                {discSummary.software.slice(0, 50).map((s, i) => (
                  <div key={i} className="flex items-center justify-between text-xs py-0.5">
                    <span className="text-gray-700 truncate max-w-[70%]">{s.name}</span>
                    <span className="text-gray-400 font-mono text-[10px]">{s.version || ''}</span>
                  </div>
                ))}
                {discSummary.softwareCount > 50 && (
                  <p className="text-xs text-gray-400 pt-1">… e mais {discSummary.softwareCount - 50} itens</p>
                )}
              </DiscoverySection>
            )}
            {/* Serviços */}
            {discSummary.serviceCount > 0 && (
              <DiscoverySection
                icon={<Monitor className="h-4 w-4 text-blue-500" />}
                label="Serviços Windows"
                count={discSummary.serviceCount}
                expanded={expandedDisc.has('svc')}
                onToggle={() => toggleDisc('svc')}
              >
                {discSummary.services.slice(0, 50).map((s, i) => (
                  <div key={i} className="flex items-center justify-between text-xs py-0.5">
                    <span className="text-gray-700 truncate max-w-[70%]">{s.name}</span>
                    <Badge
                      variant={s.status?.toLowerCase() === 'running' ? 'default' : 'secondary'}
                      className="text-[10px] py-0 h-4"
                    >
                      {s.status || '—'}
                    </Badge>
                  </div>
                ))}
                {discSummary.serviceCount > 50 && (
                  <p className="text-xs text-gray-400 pt-1">… e mais {discSummary.serviceCount - 50} itens</p>
                )}
              </DiscoverySection>
            )}
            {/* Hotfixes */}
            {discSummary.hotfixCount > 0 && (
              <DiscoverySection
                icon={<Shield className="h-4 w-4 text-green-500" />}
                label="Hotfixes / Patches"
                count={discSummary.hotfixCount}
                expanded={expandedDisc.has('hfx')}
                onToggle={() => toggleDisc('hfx')}
              >
                {discSummary.hotfixes.map((h, i) => (
                  <div key={i} className="flex items-center justify-between text-xs py-0.5">
                    <span className="font-mono text-gray-700">{h.id}</span>
                    <span className="text-gray-400">{h.installedOn || ''}</span>
                  </div>
                ))}
              </DiscoverySection>
            )}
          </CardContent>
        </Card>
      )}

      {/* Dialog para adicionar relacionamento */}
      <AddRelationshipDialog
        open={showAddDialog}
        onOpenChange={setShowAddDialog}
        sourceAssetId={assetId}
        sourceAssetName={assetName}
      />

      {/* Dialog de confirmação para deletar */}
      <AlertDialog 
        open={!!deletingRelationship} 
        onOpenChange={() => setDeletingRelationship(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remover Relacionamento</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja remover este relacionamento? Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deletingRelationship && handleDeleteRelationship(deletingRelationship)}
              className="bg-red-600 hover:bg-red-700"
            >
              Remover
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
